import React, { useState, useEffect } from 'react';
import { apiClient } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Plus, Trash2, Save, AlertTriangle, Zap, X, ChevronDown, ChevronUp, Search, Copy } from 'lucide-react';
import { toast } from 'sonner';

// ==================== FILTER CONDITIONS (same as table filters) ====================
const FILTER_CONDITIONS = [
  { key: 'ltp', label: 'LTP (Price)', type: 'number', unit: '₹' },
  { key: 'ltp_diff', label: 'LTP Diff', type: 'number', unit: '₹' },
  { key: 'ltq', label: 'LTQ', type: 'number', unit: '' },
  { key: 'bid_qty', label: 'Bid Qty', type: 'number', unit: '' },
  { key: 'ask_qty', label: 'Ask Qty', type: 'number', unit: '' },
  { key: 'bid_price', label: 'Bid Price', type: 'number', unit: '₹' },
  { key: 'ask_price', label: 'Ask Price', type: 'number', unit: '₹' },
  { key: 'spread', label: 'Spread', type: 'number', unit: '₹' },
  { key: 'volume', label: 'Volume', type: 'number', unit: '' },
  { key: 'volume_change', label: 'Volume Change', type: 'number', unit: '' },
  { key: 'volume_percent', label: 'Volume %', type: 'percent', unit: '%' },
  { key: 'oi', label: 'OI', type: 'number', unit: '' },
  { key: 'oi_change', label: 'OI Change', type: 'number', unit: '' },
  { key: 'agg_buy', label: 'Agg Buy', type: 'number', unit: '' },
  { key: 'agg_sell', label: 'Agg Sell', type: 'number', unit: '' },
  { key: 'agg_percent', label: 'Agg %', type: 'percent', unit: '%' },
  { key: 'trade_pos', label: 'Trade Position %', type: 'percent', unit: '%' },
];

const OPERATORS = [
  { key: 'gt', label: '>', description: 'Greater than' },
  { key: 'gte', label: '>=', description: 'Greater or equal' },
  { key: 'lt', label: '<', description: 'Less than' },
  { key: 'lte', label: '<=', description: 'Less or equal' },
  { key: 'eq', label: '=', description: 'Equal to' },
  { key: 'neq', label: '!=', description: 'Not equal' },
];

// ==================== SINGLE FILTER ROW COMPONENT ====================
const FilterRow = ({ filter, index, onUpdate, onRemove, isLast, showLogic }) => {
  return (
    <div className="flex flex-wrap items-center gap-2 p-2 bg-[#1a1a1a] rounded border border-[#333]">
      {/* Condition Type Dropdown */}
      <Select
        value={filter.field}
        onValueChange={(v) => onUpdate(index, 'field', v)}
      >
        <SelectTrigger className="w-28 h-7 text-[10px] bg-[#111] border-[#444]">
          <SelectValue placeholder="Field" />
        </SelectTrigger>
        <SelectContent className="bg-[#111] border-[#333] max-h-60 z-[100]">
          {FILTER_CONDITIONS.map((f) => (
            <SelectItem key={f.key} value={f.key} className="text-[11px]">
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {/* Operator Dropdown */}
      <Select
        value={filter.operator}
        onValueChange={(v) => onUpdate(index, 'operator', v)}
      >
        <SelectTrigger className="w-16 h-7 text-[10px] bg-[#111] border-[#444]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#111] border-[#333] z-[100]">
          {OPERATORS.map((op) => (
            <SelectItem key={op.key} value={op.key} className="text-[11px]">
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {/* Value Input */}
      <Input
        type="number"
        value={filter.value}
        onChange={(e) => onUpdate(index, 'value', parseFloat(e.target.value) || 0)}
        className="w-20 h-7 text-[10px] bg-[#111] border-[#444] text-center"
        placeholder="Value"
      />
      
      {/* Unit display */}
      <span className="text-[10px] text-[#666] w-4">
        {FILTER_CONDITIONS.find(f => f.key === filter.field)?.unit || ''}
      </span>
      
      {/* Logic connector (AND/OR) - only show if not last */}
      {showLogic && !isLast && (
        <Select
          value={filter.logic || 'AND'}
          onValueChange={(v) => onUpdate(index, 'logic', v)}
        >
          <SelectTrigger className="w-14 h-7 text-[9px] bg-purple-900/30 border-purple-500/40 text-purple-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-[#333] z-[100]">
            <SelectItem value="AND" className="text-[11px] text-green-400">AND</SelectItem>
            <SelectItem value="OR" className="text-[11px] text-yellow-400">OR</SelectItem>
          </SelectContent>
        </Select>
      )}
      
      {/* Remove button */}
      <button
        onClick={() => onRemove(index)}
        className="h-6 w-6 flex items-center justify-center text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded ml-auto"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

// ==================== SYMBOL SELECTOR COMPONENT ====================
const SymbolSelector = ({ selectedSymbols = [], watchlistSymbols = [], onUpdate, mode = 'all', setMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Normalize watchlistSymbols - handle both string array and object array
  const normalizedSymbols = React.useMemo(() => {
    if (!watchlistSymbols || !Array.isArray(watchlistSymbols) || watchlistSymbols.length === 0) return [];
    
    return watchlistSymbols
      .filter(s => s) // Filter out null/undefined
      .map(s => {
        if (typeof s === 'string') {
          return { tradingsymbol: s, token: s };
        }
        return { tradingsymbol: s.tradingsymbol || s, token: s.token || s.tradingsymbol || s };
      });
  }, [watchlistSymbols]);
  
  // Ensure selectedSymbols is always an array
  const safeSelectedSymbols = Array.isArray(selectedSymbols) ? selectedSymbols : [];
  
  const filteredSymbols = normalizedSymbols.filter(s => 
    s.tradingsymbol?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const toggleSymbol = (symbol) => {
    if (!onUpdate) return;
    const newSelected = safeSelectedSymbols.includes(symbol)
      ? safeSelectedSymbols.filter(s => s !== symbol)
      : [...safeSelectedSymbols, symbol];
    onUpdate(newSelected);
  };
  
  const selectAll = () => {
    if (!onUpdate) return;
    onUpdate(normalizedSymbols.map(s => s.tradingsymbol));
  };
  
  const clearAll = () => {
    if (!onUpdate) return;
    onUpdate([]);
  };
  
  return (
    <div className="space-y-2">
      {/* Mode Selection */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-[#888]">Apply to:</span>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => { setMode('all'); onUpdate([]); }}
            className={`px-3 py-1.5 text-[10px] rounded transition-all ${
              mode === 'all' 
                ? 'bg-green-500/30 text-green-300 border border-green-400' 
                : 'bg-[#222] text-[#888] border border-[#444] hover:bg-[#333]'
            }`}
          >
            All Watchlist
          </button>
          <button
            type="button"
            onClick={() => setMode('selected')}
            className={`px-3 py-1.5 text-[10px] rounded transition-all ${
              mode === 'selected' 
                ? 'bg-blue-500/30 text-blue-300 border border-blue-400' 
                : 'bg-[#222] text-[#888] border border-[#444] hover:bg-[#333]'
            }`}
          >
            Selected ({safeSelectedSymbols.length})
          </button>
        </div>
      </div>
      
      {/* Symbol picker (shown when mode is 'selected') */}
      {mode === 'selected' && (
        <div className="border border-[#333] rounded bg-[#0a0a0a] p-2 space-y-2">
          {/* Search and actions */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#666]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search symbols..."
                className="w-full h-7 pl-7 pr-2 text-[10px] bg-[#111] border border-[#444] rounded text-white placeholder:text-[#555]"
              />
            </div>
            <button 
              type="button"
              onClick={selectAll} 
              className="text-[9px] text-blue-400 hover:text-blue-300 px-2 py-1 rounded hover:bg-blue-500/10"
            >
              All
            </button>
            <button 
              type="button"
              onClick={clearAll} 
              className="text-[9px] text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/10"
            >
              Clear
            </button>
            <button 
              type="button"
              onClick={() => setIsExpanded(!isExpanded)} 
              className="text-[#888] p-1 hover:bg-[#333] rounded"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          
          {/* Selected chips */}
          {safeSelectedSymbols.length > 0 && (
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto p-1 bg-[#111] rounded">
              {safeSelectedSymbols.map(sym => (
                <span
                  key={sym}
                  className="inline-flex items-center gap-1 text-[9px] px-2 py-1 bg-blue-500/20 text-blue-300 border border-blue-400/50 rounded cursor-pointer hover:bg-red-500/20 hover:text-red-300 hover:border-red-400"
                  onClick={() => toggleSymbol(sym)}
                >
                  {sym}
                  <X className="w-2.5 h-2.5" />
                </span>
              ))}
            </div>
          )}
          
          {/* Symbol list */}
          {isExpanded && normalizedSymbols.length > 0 && (
            <div className="max-h-32 overflow-y-auto border-t border-[#333] pt-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                {filteredSymbols.map(sym => (
                  <label
                    key={sym.tradingsymbol}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#222] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={safeSelectedSymbols.includes(sym.tradingsymbol)}
                      onChange={() => toggleSymbol(sym.tradingsymbol)}
                      className="w-3 h-3 rounded border-[#444] bg-[#111] text-blue-500"
                    />
                    <span className="text-[10px] text-white truncate">{sym.tradingsymbol}</span>
                  </label>
                ))}
              </div>
              {filteredSymbols.length === 0 && (
                <p className="text-[10px] text-[#666] text-center py-2">No symbols found</p>
              )}
            </div>
          )}
          
          {normalizedSymbols.length === 0 && (
            <p className="text-[10px] text-yellow-400 text-center py-2">
              No symbols in watchlist. Add symbols first.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== SINGLE CONDITION CARD COMPONENT ====================
const ConditionCard = ({ condition, index, onUpdate, onRemove, onDuplicate, watchlistSymbols }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Update a single filter within this condition
  const updateFilter = (filterIndex, field, value) => {
    const updatedFilters = [...(condition.filters || [])];
    updatedFilters[filterIndex] = { ...updatedFilters[filterIndex], [field]: value };
    onUpdate(index, 'filters', updatedFilters);
  };
  
  // Add new filter to this condition
  const addFilter = () => {
    const newFilter = { field: 'ltq', operator: 'gt', value: 100, logic: 'AND' };
    onUpdate(index, 'filters', [...(condition.filters || []), newFilter]);
  };
  
  // Remove filter from this condition
  const removeFilter = (filterIndex) => {
    const updatedFilters = (condition.filters || []).filter((_, i) => i !== filterIndex);
    onUpdate(index, 'filters', updatedFilters);
  };
  
  // Get condition preview text
  const getPreviewText = () => {
    const filters = condition.filters || [];
    if (filters.length === 0) return 'No filters set';
    
    return filters.map((f, i) => {
      const fieldLabel = FILTER_CONDITIONS.find(fc => fc.key === f.field)?.label || f.field;
      const opLabel = OPERATORS.find(o => o.key === f.operator)?.label || f.operator;
      const logic = i < filters.length - 1 ? ` ${f.logic || 'AND'} ` : '';
      return `${fieldLabel} ${opLabel} ${f.value}${logic}`;
    }).join('');
  };
  
  return (
    <div className={`border rounded-lg overflow-hidden ${
      condition.enabled ? 'border-[#444] bg-[#111]' : 'border-[#333] bg-[#0a0a0a] opacity-60'
    }`}>
      {/* Condition Header */}
      <div 
        className="flex items-center justify-between p-3 bg-[#0d0d0d] cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <Switch
            checked={condition.enabled}
            onCheckedChange={(v) => { onUpdate(index, 'enabled', v); }}
            onClick={(e) => e.stopPropagation()}
            className="scale-75 data-[state=checked]:bg-green-500"
          />
          <span className="text-[11px] font-medium text-white">Alert #{index + 1}</span>
          <span className="text-[9px] px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
            {(condition.filters || []).length} filter{(condition.filters || []).length !== 1 ? 's' : ''}
          </span>
          {condition.tick_window > 1 && (
            <span className="text-[9px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">
              {condition.tick_window} ticks
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}
            className="h-6 w-6 flex items-center justify-center text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded"
            title="Duplicate"
          >
            <Copy className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(index); }}
            className="h-6 w-6 flex items-center justify-center text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          {isExpanded ? <ChevronUp className="w-4 h-4 text-[#666]" /> : <ChevronDown className="w-4 h-4 text-[#666]" />}
        </div>
      </div>
      
      {/* Preview when collapsed */}
      {!isExpanded && (
        <div className="px-3 pb-2 text-[10px] text-cyan-400 bg-cyan-500/5 truncate">
          {getPreviewText()}
        </div>
      )}
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-3 space-y-3 border-t border-[#333]">
          {/* Filter List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-[#888] font-medium">CONDITIONS</span>
              <button
                type="button"
                onClick={addFilter}
                className="h-6 px-2 text-[9px] bg-[#1a1a1a] border border-[#444] hover:bg-[#222] rounded flex items-center gap-1"
              >
                <Plus className="w-2.5 h-2.5" /> Add Filter
              </button>
            </div>
            
            {(condition.filters || []).length === 0 ? (
              <div className="text-[10px] text-[#666] text-center py-3 bg-[#0a0a0a] rounded">
                Click "Add Filter" to create conditions
              </div>
            ) : (
              <div className="space-y-1.5">
                {(condition.filters || []).map((filter, fIdx) => (
                  <FilterRow
                    key={fIdx}
                    filter={filter}
                    index={fIdx}
                    onUpdate={updateFilter}
                    onRemove={removeFilter}
                    isLast={fIdx === (condition.filters || []).length - 1}
                    showLogic={(condition.filters || []).length > 1}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Tick Window */}
          <div className="flex items-center gap-2 p-2 bg-purple-500/10 rounded border border-purple-500/30">
            <span className="text-[10px] text-purple-300">Within last</span>
            <input
              type="number"
              value={condition.tick_window || 1}
              onChange={(e) => onUpdate(index, 'tick_window', parseInt(e.target.value) || 1)}
              className="w-14 h-6 text-[10px] bg-[#111] border border-purple-500/40 text-center rounded text-white"
              min="1"
              max="100"
            />
            <span className="text-[10px] text-purple-300">ticks</span>
            <span className="text-[9px] text-[#666] ml-1">(1 = instant)</span>
          </div>
          
          {/* Symbol Selection */}
          <div className="pt-2 border-t border-[#333]">
            <SymbolSelector
              selectedSymbols={condition.symbols || []}
              watchlistSymbols={watchlistSymbols}
              onUpdate={(syms) => onUpdate(index, 'symbols', syms)}
              mode={condition.symbol_mode || 'all'}
              setMode={(m) => onUpdate(index, 'symbol_mode', m)}
            />
          </div>
          
          {/* Cooldown */}
          <div className="flex items-center gap-2 pt-2 border-t border-[#333]">
            <span className="text-[10px] text-[#888]">Cooldown:</span>
            <input
              type="number"
              value={condition.cooldown_seconds ?? 1}
              onChange={(e) => onUpdate(index, 'cooldown_seconds', parseInt(e.target.value) || 0)}
              className="w-16 h-6 text-[10px] bg-[#111] border border-[#444] text-center rounded text-white"
              min="0"
              max="3600"
            />
            <span className="text-[10px] text-[#888]">sec {condition.cooldown_seconds <= 1 ? '(per-tick)' : ''}</span>
          </div>
          
          {/* Preview */}
          <div className="text-[10px] text-green-400 bg-green-500/10 rounded px-2 py-1.5 border border-green-500/20">
            <strong>Alert when:</strong> {getPreviewText()}
            {condition.tick_window > 1 && <span className="text-purple-400"> within {condition.tick_window} ticks</span>}
            <br />
            <span className="text-[#888]">
              On: {condition.symbol_mode === 'all' || (condition.symbols || []).length === 0 
                ? 'ALL watchlist symbols' 
                : `${(condition.symbols || []).length} selected symbols`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN SERVER ALERT MANAGER COMPONENT ====================
const ServerAlertManager = ({ watchlistSymbols = [], onClose }) => {
  const [conditions, setConditions] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Load existing conditions
  useEffect(() => {
    const loadConditions = async () => {
      try {
        setError(null);
        const response = await apiClient.get('/user/server-alerts/conditions');
        if (response.data) {
          const loadedConditions = (response.data.conditions || []).map(c => {
            // Convert old format to new format if needed
            if (c.condition_type && !c.filters) {
              return {
                ...c,
                filters: [{
                  field: c.condition_type,
                  operator: c.operator,
                  value: c.value,
                  logic: 'AND'
                }],
                tick_window: 1,
                symbol_mode: c.symbols?.length > 0 ? 'selected' : 'all'
              };
            }
            return {
              ...c,
              filters: c.filters || [],
              tick_window: c.tick_window || 1,
              symbol_mode: c.symbol_mode || (c.symbols?.length > 0 ? 'selected' : 'all')
            };
          });
          setConditions(loadedConditions);
          setEnabled(response.data.enabled || false);
        }
      } catch (err) {
        console.error('Failed to load server alert conditions:', err);
        setError('Failed to load alerts. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadConditions();
  }, []);
  
  // Add new condition
  const addCondition = () => {
    setConditions([
      ...conditions,
      {
        filters: [{ field: 'ltq', operator: 'gt', value: 100, logic: 'AND' }],
        symbols: [],
        symbol_mode: 'all',
        enabled: true,
        cooldown_seconds: 1,  // Default 1 second for per-tick alerts
        tick_window: 1
      }
    ]);
  };
  
  // Duplicate condition
  const duplicateCondition = (index) => {
    const original = conditions[index];
    const duplicate = JSON.parse(JSON.stringify(original));
    setConditions([...conditions.slice(0, index + 1), duplicate, ...conditions.slice(index + 1)]);
    toast.success('Condition duplicated');
  };
  
  // Remove condition
  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };
  
  // Update condition
  const updateCondition = (index, field, value) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], [field]: value };
    setConditions(updated);
  };
  
  // Save conditions
  const saveConditions = async () => {
    setSaving(true);
    try {
      const conditionsToSave = conditions.map(c => ({
        filters: c.filters || [],
        symbols: c.symbol_mode === 'all' ? [] : (c.symbols || []),
        symbol_mode: c.symbol_mode || 'all',
        enabled: c.enabled !== false,
        cooldown_seconds: c.cooldown_seconds ?? 1,  // Default 1 second for per-tick alerts
        tick_window: c.tick_window || 1
      }));
      
      await apiClient.post('/user/server-alerts/conditions', {
        conditions: conditionsToSave
      });
      toast.success('Server alert conditions saved!');
    } catch (err) {
      toast.error('Failed to save conditions');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle all alerts
  const toggleAlerts = async (newEnabled) => {
    try {
      await apiClient.post(`/user/server-alerts/toggle?enabled=${newEnabled}`);
      setEnabled(newEnabled);
      toast.success(newEnabled ? 'Server alerts enabled' : 'Server alerts disabled');
    } catch (err) {
      toast.error('Failed to toggle alerts');
    }
  };
  
  if (loading) {
    return (
      <div className="p-4 text-center text-[#888]">
        <div className="animate-spin w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full mx-auto mb-2" />
        Loading...
      </div>
    );
  }
  
  return (
    <div className="bg-[#0d0d0d] border border-[#333] rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-[#333] bg-gradient-to-r from-yellow-900/20 to-orange-900/20">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-400" />
          <h3 className="text-sm font-bold text-white">Server-Side Alerts</h3>
          <span className="text-[9px] px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
            24/7
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-[#888]">
            {enabled ? <span className="text-green-400">ON</span> : 'OFF'}
          </span>
          <Switch
            checked={enabled}
            onCheckedChange={toggleAlerts}
            className="data-[state=checked]:bg-green-500"
          />
          {onClose && (
            <button onClick={onClose} className="text-[#666] hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-3 space-y-3 max-h-[60vh] overflow-y-auto">
        {/* Info */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 text-[10px] text-blue-300">
          <AlertTriangle className="w-3 h-3 inline mr-1" />
          Alerts trigger even when app is closed. Configure Telegram in settings.
        </div>
        
        {/* Error display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-[10px] text-red-300">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            {error}
          </div>
        )}
        
        {/* Conditions */}
        {conditions.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-[#444] rounded-lg bg-[#0a0a0a]">
            <Zap className="w-8 h-8 text-[#444] mx-auto mb-2" />
            <p className="text-[#666] text-sm">No alert conditions</p>
            <p className="text-[#555] text-[10px] mt-1">Click "Add Alert" to create one</p>
          </div>
        ) : (
          <div className="space-y-2">
            {conditions.map((condition, index) => (
              <ConditionCard
                key={index}
                condition={condition}
                index={index}
                onUpdate={updateCondition}
                onRemove={removeCondition}
                onDuplicate={duplicateCondition}
                watchlistSymbols={watchlistSymbols}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between p-3 border-t border-[#333] bg-[#080808]">
        <button
          type="button"
          onClick={addCondition}
          className="h-8 px-3 text-[11px] bg-[#1a1a1a] border border-[#444] hover:bg-[#222] rounded flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add Alert
        </button>
        
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#666]">{conditions.length} alert(s)</span>
          <button
            type="button"
            onClick={saveConditions}
            disabled={saving}
            className="h-8 px-4 text-[11px] bg-green-600 hover:bg-green-500 text-white rounded flex items-center gap-1 disabled:opacity-50"
          >
            <Save className="w-3 h-3" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServerAlertManager;
